import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  GitCommit, Rocket, CheckCircle2, AlertTriangle, UserPlus, UserMinus,
  MessageSquare, RefreshCw, Filter, ChevronDown, Loader2,
} from 'lucide-react';
import logger from '../utils/logger';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMM_HUB_BASE = import.meta.env.DEV
  ? 'http://localhost:3030'
  : 'https://apps.quantrolix.ai/api/comm-hub';

const FEED_ITEM_TYPES = {
  task_completed: { icon: CheckCircle2, color: '#22c55e', bg: 'rgba(34,197,94,0.12)', label: 'Task' },
  deploy: { icon: Rocket, color: '#a855f7', bg: 'rgba(168,85,247,0.12)', label: 'Deploy' },
  commit: { icon: GitCommit, color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', label: 'Commit' },
  agent_joined: { icon: UserPlus, color: '#06b6d4', bg: 'rgba(6,182,212,0.12)', label: 'Agent' },
  agent_left: { icon: UserMinus, color: '#6b7280', bg: 'rgba(107,114,128,0.12)', label: 'Agent' },
  alert: { icon: AlertTriangle, color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'Alert' },
  message: { icon: MessageSquare, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Message' },
  build_status: { icon: Rocket, color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', label: 'Build' },
  handoff: { icon: MessageSquare, color: '#667eea', bg: 'rgba(102,126,234,0.12)', label: 'Handoff' },
};

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'task_completed', label: 'Tasks' },
  { key: 'deploy', label: 'Deploys' },
  { key: 'commit', label: 'Commits' },
  { key: 'alert', label: 'Alerts' },
  { key: 'message', label: 'Messages' },
];

const AUTO_REFRESH_MS = 30000;
const PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function groupByTimePeriod(items) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 86400000;
  const weekStart = todayStart - 6 * 86400000;

  const groups = {
    'Today': [],
    'Yesterday': [],
    'This Week': [],
    'Older': [],
  };

  items.forEach((item) => {
    const ts = new Date(item.timestamp).getTime();
    if (ts >= todayStart) groups['Today'].push(item);
    else if (ts >= yesterdayStart) groups['Yesterday'].push(item);
    else if (ts >= weekStart) groups['This Week'].push(item);
    else groups['Older'].push(item);
  });

  return Object.entries(groups).filter(([, items]) => items.length > 0);
}

/**
 * Fetch JSON from Comm Hub with timeout and error handling
 */
async function commHubFetch(path) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(`${COMM_HUB_BASE}${path}`, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(timeout);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    clearTimeout(timeout);
    throw err;
  }
}

/**
 * Normalize data from three Comm Hub endpoints into a unified feed.
 */
function normalizeTasks(tasks) {
  if (!Array.isArray(tasks)) return [];
  return tasks
    .filter((t) => t.status === 'completed')
    .map((t) => ({
      id: `task-${t.id}`,
      type: 'task_completed',
      title: t.title || `Task #${t.id} completed`,
      description: t.description || '',
      project: t.project || '',
      actor: t.claimed_by || t.claudeId || 'system',
      timestamp: t.completed_at || t.updated_at || t.created_at,
    }));
}

function normalizeActivity(activity) {
  if (!Array.isArray(activity)) {
    // Comm Hub may return {agents: []} or flat array
    if (activity && Array.isArray(activity.agents)) activity = activity.agents;
    else if (activity && Array.isArray(activity.services)) activity = activity.services;
    else return [];
  }
  return activity.map((a, idx) => {
    const status = (a.status || '').toLowerCase();
    const isDeploy = status.includes('deploy');
    const isJoined = status === 'active' || status === 'working' || status === 'idle';
    let type = 'agent_joined';
    if (isDeploy) type = 'deploy';
    else if (status === 'offline' || status === 'left') type = 'agent_left';
    else if (isJoined) type = 'agent_joined';

    return {
      id: `activity-${a.claudeId || a.id || idx}-${Date.now()}`,
      type,
      title: isDeploy
        ? `${a.project || 'App'} deployed`
        : isJoined
          ? `${a.claudeId || 'Agent'} is ${status}`
          : `${a.claudeId || 'Agent'} went ${status}`,
      description: a.files ? `Working on: ${Array.isArray(a.files) ? a.files.join(', ') : a.files}` : '',
      project: a.project || '',
      actor: a.claudeId || a.name || 'unknown',
      timestamp: a.lastSeen || a.timestamp || new Date().toISOString(),
    };
  });
}

function normalizeMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.map((m) => {
    const msgType = (m.type || 'message').toLowerCase();
    let type = 'message';
    if (msgType === 'alert') type = 'alert';
    else if (msgType === 'handoff') type = 'handoff';
    else if (msgType === 'build_status') type = 'build_status';
    else if (msgType === 'deploy_notice') type = 'deploy';

    return {
      id: `msg-${m.id}`,
      type,
      title: type === 'alert'
        ? (m.message || 'Alert')
        : type === 'handoff'
          ? `Handoff from ${m.from || 'agent'}`
          : type === 'build_status'
            ? `Build: ${m.message || 'status update'}`
            : (m.message || 'Message'),
      description: m.reply || m.message || '',
      project: m.project || '',
      actor: m.from || 'system',
      timestamp: m.created_at || m.timestamp || new Date().toISOString(),
    };
  });
}

/**
 * Generate realistic mock activity data for when Comm Hub is unavailable
 */
function generateMockFeed() {
  const agents = ['claude-opus-1', 'gemini-1', 'codex-1', 'claude-sonnet-2'];
  const projects = ['fire-pos', 'quantrolix-saas', 'tea-effects', 'grocery-optimizer', 'phantomcast', 'comm-hub'];
  const taskTitles = [
    'Add tip calculation UI', 'Fix WebSocket reconnection', 'Update pricing modal',
    'Implement dark mode toggle', 'Add Redis caching layer', 'Fix CORS headers',
    'Add unit tests for auth', 'Optimize database queries', 'Update API documentation',
    'Add user avatar upload', 'Fix memory leak in store', 'Implement SSO flow',
  ];
  const deployMessages = [
    'deployed to production', 'deployed staging build', 'hot-fix deployed',
    'rolled back to v2.1.3', 'canary deploy started',
  ];
  const alertMessages = [
    'Build failed for tea-effects', 'High memory usage on fire-pos',
    'SSL certificate expiring in 7 days', 'Database connection pool exhausted',
  ];

  const items = [];
  const now = Date.now();

  // Generate 30 mock items spread across the last 5 days
  for (let i = 0; i < 30; i++) {
    const ageMs = Math.floor(Math.random() * 5 * 24 * 60 * 60 * 1000);
    const timestamp = new Date(now - ageMs).toISOString();
    const agent = agents[Math.floor(Math.random() * agents.length)];
    const project = projects[Math.floor(Math.random() * projects.length)];

    const rand = Math.random();
    if (rand < 0.35) {
      const title = taskTitles[Math.floor(Math.random() * taskTitles.length)];
      items.push({
        id: `mock-task-${i}`,
        type: 'task_completed',
        title: `${agent} completed '${title}'`,
        description: title,
        project,
        actor: agent,
        timestamp,
      });
    } else if (rand < 0.55) {
      const msg = deployMessages[Math.floor(Math.random() * deployMessages.length)];
      items.push({
        id: `mock-deploy-${i}`,
        type: 'deploy',
        title: `${project} ${msg}`,
        description: `Initiated by ${agent}`,
        project,
        actor: agent,
        timestamp,
      });
    } else if (rand < 0.7) {
      items.push({
        id: `mock-agent-${i}`,
        type: Math.random() > 0.3 ? 'agent_joined' : 'agent_left',
        title: `${agent} ${Math.random() > 0.3 ? 'joined' : 'left'} the swarm`,
        description: '',
        project: '',
        actor: agent,
        timestamp,
      });
    } else if (rand < 0.85) {
      const alert = alertMessages[Math.floor(Math.random() * alertMessages.length)];
      items.push({
        id: `mock-alert-${i}`,
        type: 'alert',
        title: alert,
        description: '',
        project,
        actor: 'system',
        timestamp,
      });
    } else {
      items.push({
        id: `mock-msg-${i}`,
        type: 'message',
        title: `${agent}: Review requested for ${project}`,
        description: 'Please review the latest changes',
        project,
        actor: agent,
        timestamp,
      });
    }
  }

  return items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

// ---------------------------------------------------------------------------
// ActivityFeed Component
// ---------------------------------------------------------------------------

const ActivityFeed = ({ compact = false, maxItems = 0 }) => {
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [animatingIds, setAnimatingIds] = useState(new Set());
  const intervalRef = useRef(null);
  const prevIdsRef = useRef(new Set());
  const dropdownRef = useRef(null);

  // Close project dropdown on outside click
  useEffect(() => {
    if (!showProjectDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showProjectDropdown]);

  /**
   * Fetch feed data from Comm Hub (tasks, activity, messages)
   */
  const fetchFeed = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        commHubFetch('/api/tasks'),
        commHubFetch('/api/activity'),
        commHubFetch('/api/messages'),
      ]);

      const tasks = results[0].status === 'fulfilled' ? results[0].value : [];
      const activity = results[1].status === 'fulfilled' ? results[1].value : [];
      const messages = results[2].status === 'fulfilled' ? results[2].value : [];

      const anySuccess = results.some((r) => r.status === 'fulfilled');

      const normalized = [
        ...normalizeTasks(tasks),
        ...normalizeActivity(activity),
        ...normalizeMessages(messages),
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      if (anySuccess && normalized.length > 0) {
        // Detect new items for animation
        const currentIds = new Set(normalized.map((item) => item.id));
        const newIds = new Set();
        currentIds.forEach((id) => {
          if (!prevIdsRef.current.has(id)) newIds.add(id);
        });
        prevIdsRef.current = currentIds;

        if (newIds.size > 0 && feedItems.length > 0) {
          setAnimatingIds(newIds);
          setTimeout(() => setAnimatingIds(new Set()), 600);
        }

        setFeedItems(normalized);
        setIsLive(true);
        setError(null);
      } else if (!anySuccess) {
        throw new Error('All Comm Hub endpoints unreachable');
      } else {
        // Some succeeded but no data
        setFeedItems(normalized);
        setIsLive(true);
        setError(null);
      }

      setLoading(false);
    } catch (err) {
      logger.debug('Activity feed using mock data:', err.message);
      // Only set mock data if we have no existing data
      if (feedItems.length === 0) {
        const mockData = generateMockFeed();
        setFeedItems(mockData);
        prevIdsRef.current = new Set(mockData.map((item) => item.id));
      }
      setIsLive(false);
      setError(null); // Don't show error for expected fallback
      setLoading(false);
    }
  }, [feedItems.length]);

  // Initial fetch + auto-refresh
  useEffect(() => {
    fetchFeed();

    intervalRef.current = setInterval(fetchFeed, AUTO_REFRESH_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchFeed]);

  // Derive unique projects for filter dropdown
  const uniqueProjects = [...new Set(feedItems.map((i) => i.project).filter(Boolean))].sort();

  // Apply filters
  const filteredItems = feedItems.filter((item) => {
    if (typeFilter !== 'all' && item.type !== typeFilter) return false;
    if (projectFilter !== 'all' && item.project !== projectFilter) return false;
    return true;
  });

  // Apply limit
  const displayItems = maxItems > 0
    ? filteredItems.slice(0, maxItems)
    : filteredItems.slice(0, visibleCount);

  const hasMore = maxItems > 0
    ? false
    : visibleCount < filteredItems.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  // Group for time sections
  const grouped = groupByTimePeriod(displayItems);

  // Loading state
  if (loading) {
    return (
      <div className="af-widget">
        <div className="af-loading">
          <Loader2 className="af-spin" style={{ width: 24, height: 24, color: '#00d4ff' }} />
          <span>Loading activity feed...</span>
        </div>
        <style>{feedStyles}</style>
      </div>
    );
  }

  return (
    <div className={`af-widget ${compact ? 'af-compact' : ''}`}>
      {/* Header */}
      <div className="af-header">
        <div className="af-header-left">
          <div className="af-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <h2 className="af-title">Activity Feed</h2>
          <span className="af-badge">{filteredItems.length} events</span>
        </div>
        <div className="af-header-right">
          <div className={`af-live-badge ${isLive ? 'live' : 'mock'}`}>
            <span className="af-live-dot" />
            <span>{isLive ? 'Live' : 'Demo'}</span>
          </div>
          <button className="af-refresh-btn" onClick={fetchFeed} title="Refresh now">
            <RefreshCw style={{ width: 15, height: 15 }} />
          </button>
        </div>
      </div>

      {/* Filters */}
      {!compact && (
        <div className="af-filters">
          <div className="af-type-filters">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.key}
                className={`af-type-pill ${typeFilter === f.key ? 'active' : ''}`}
                onClick={() => {
                  setTypeFilter(f.key);
                  setVisibleCount(PAGE_SIZE);
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="af-project-filter" ref={dropdownRef}>
            <button
              className="af-project-btn"
              onClick={() => setShowProjectDropdown(!showProjectDropdown)}
            >
              <Filter style={{ width: 13, height: 13 }} />
              <span>{projectFilter === 'all' ? 'All Projects' : projectFilter}</span>
              <ChevronDown style={{ width: 13, height: 13 }} />
            </button>
            {showProjectDropdown && (
              <div className="af-project-dropdown">
                <button
                  className={`af-project-option ${projectFilter === 'all' ? 'active' : ''}`}
                  onClick={() => { setProjectFilter('all'); setShowProjectDropdown(false); setVisibleCount(PAGE_SIZE); }}
                >
                  All Projects
                </button>
                {uniqueProjects.map((p) => (
                  <button
                    key={p}
                    className={`af-project-option ${projectFilter === p ? 'active' : ''}`}
                    onClick={() => { setProjectFilter(p); setShowProjectDropdown(false); setVisibleCount(PAGE_SIZE); }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feed Content */}
      {filteredItems.length === 0 ? (
        <div className="af-empty">
          <div className="af-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <p className="af-empty-title">No activity yet</p>
          <p className="af-empty-desc">
            {typeFilter !== 'all' || projectFilter !== 'all'
              ? 'Try adjusting your filters to see more activity.'
              : 'Activity from tasks, deploys, and agents will appear here.'}
          </p>
        </div>
      ) : (
        <div className="af-timeline">
          {grouped.map(([period, items]) => (
            <div key={period} className="af-time-group">
              <div className="af-time-label">{period}</div>
              {items.map((item) => {
                const typeConfig = FEED_ITEM_TYPES[item.type] || FEED_ITEM_TYPES.message;
                const IconComponent = typeConfig.icon;
                const isNew = animatingIds.has(item.id);

                return (
                  <div
                    key={item.id}
                    className={`af-item ${isNew ? 'af-item-enter' : ''}`}
                  >
                    {/* Timeline line */}
                    <div className="af-item-line" />

                    {/* Icon */}
                    <div
                      className="af-item-icon"
                      style={{ background: typeConfig.bg, color: typeConfig.color }}
                    >
                      <IconComponent style={{ width: 16, height: 16 }} />
                    </div>

                    {/* Content */}
                    <div className="af-item-content">
                      <div className="af-item-header">
                        <span className="af-item-title">{item.title}</span>
                        <span className="af-item-time">{relativeTime(item.timestamp)}</span>
                      </div>
                      {item.description && !compact && (
                        <p className="af-item-desc">{item.description}</p>
                      )}
                      <div className="af-item-meta">
                        {item.project && (
                          <span
                            className="af-item-project"
                            onClick={() => {
                              if (!compact) {
                                setProjectFilter(item.project);
                                setVisibleCount(PAGE_SIZE);
                              }
                            }}
                            style={{ cursor: compact ? 'default' : 'pointer' }}
                          >
                            {item.project}
                          </span>
                        )}
                        {item.actor && item.actor !== 'system' && (
                          <span className="af-item-actor">{item.actor}</span>
                        )}
                        <span
                          className="af-item-type-badge"
                          style={{ color: typeConfig.color, background: typeConfig.bg }}
                        >
                          {typeConfig.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Load more */}
          {hasMore && (
            <button className="af-load-more" onClick={handleLoadMore}>
              Load more ({filteredItems.length - visibleCount} remaining)
            </button>
          )}
        </div>
      )}

      <style>{feedStyles}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const feedStyles = `
  .af-widget {
    background: rgba(10, 10, 15, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 20px;
    padding: 24px;
    animation: afFadeIn 0.4s ease-out;
  }

  .af-widget.af-compact {
    padding: 18px;
    border-radius: 16px;
  }

  @keyframes afFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .af-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px 0;
    color: rgba(255,255,255,0.6);
    font-size: 0.9rem;
  }

  .af-spin {
    animation: afSpin 1s linear infinite;
  }

  @keyframes afSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* Header */
  .af-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .af-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .af-header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .af-title {
    font-family: 'Orbitron', monospace;
    font-size: 1.1rem;
    font-weight: 600;
    background: linear-gradient(135deg, #667eea, #00d4ff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .af-compact .af-title {
    font-size: 0.95rem;
  }

  .af-badge {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.4);
    background: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 8px;
    padding: 2px 8px;
  }

  .af-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .af-live-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    padding: 3px 10px;
    border-radius: 6px;
    font-weight: 500;
  }

  .af-live-badge.live {
    color: #22c55e;
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.3);
  }

  .af-live-badge.mock {
    color: #eab308;
    background: rgba(234, 179, 8, 0.1);
    border: 1px solid rgba(234, 179, 8, 0.3);
  }

  .af-live-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    animation: afPulse 2s infinite;
  }

  .af-live-badge.live .af-live-dot {
    background: #22c55e;
    box-shadow: 0 0 6px #22c55e;
  }

  .af-live-badge.mock .af-live-dot {
    background: #eab308;
    box-shadow: 0 0 6px #eab308;
  }

  @keyframes afPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.4); }
  }

  .af-refresh-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 8px;
    color: #00d4ff;
    cursor: pointer;
    transition: all 0.2s;
  }

  .af-refresh-btn:hover {
    background: rgba(0, 212, 255, 0.2);
    transform: rotate(90deg);
  }

  /* Filters */
  .af-filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
    flex-wrap: wrap;
  }

  .af-type-filters {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .af-type-pill {
    padding: 5px 12px;
    border-radius: 16px;
    font-size: 0.78rem;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(102, 126, 234, 0.2);
    color: rgba(255,255,255,0.6);
    cursor: pointer;
    transition: all 0.2s;
  }

  .af-type-pill:hover {
    background: rgba(102, 126, 234, 0.15);
    color: rgba(255,255,255,0.9);
  }

  .af-type-pill.active {
    background: rgba(102, 126, 234, 0.2);
    border-color: rgba(102, 126, 234, 0.5);
    color: #fff;
    font-weight: 600;
    box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.3);
  }

  .af-project-filter {
    position: relative;
  }

  .af-project-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 8px;
    font-size: 0.78rem;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(102, 126, 234, 0.2);
    color: rgba(255,255,255,0.7);
    cursor: pointer;
    transition: all 0.2s;
  }

  .af-project-btn:hover {
    background: rgba(102, 126, 234, 0.15);
  }

  .af-project-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 180px;
    max-height: 240px;
    overflow-y: auto;
    background: rgba(10, 14, 26, 0.98);
    border: 1px solid rgba(102, 126, 234, 0.3);
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    z-index: 100;
    padding: 4px;
  }

  .af-project-option {
    display: block;
    width: 100%;
    padding: 7px 12px;
    border-radius: 6px;
    font-size: 0.78rem;
    background: transparent;
    border: none;
    color: rgba(255,255,255,0.7);
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
  }

  .af-project-option:hover {
    background: rgba(102, 126, 234, 0.15);
  }

  .af-project-option.active {
    background: rgba(102, 126, 234, 0.2);
    color: #fff;
    font-weight: 600;
  }

  /* Empty State */
  .af-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 50px 20px;
    text-align: center;
  }

  .af-empty-icon {
    margin-bottom: 16px;
    opacity: 0.4;
  }

  .af-empty-title {
    font-size: 1rem;
    font-weight: 600;
    color: rgba(255,255,255,0.5);
    margin-bottom: 6px;
  }

  .af-empty-desc {
    font-size: 0.85rem;
    color: rgba(255,255,255,0.35);
    max-width: 320px;
  }

  /* Timeline */
  .af-timeline {
    position: relative;
  }

  .af-time-group {
    margin-bottom: 8px;
  }

  .af-time-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: rgba(255,255,255,0.35);
    padding: 8px 0 6px 44px;
    border-bottom: 1px solid rgba(102, 126, 234, 0.08);
    margin-bottom: 4px;
  }

  .af-compact .af-time-label {
    padding-left: 38px;
  }

  /* Feed Item */
  .af-item {
    position: relative;
    display: flex;
    gap: 12px;
    padding: 10px 0;
    transition: background 0.2s;
    border-radius: 10px;
    padding-left: 4px;
    padding-right: 4px;
  }

  .af-compact .af-item {
    gap: 10px;
    padding: 7px 4px;
  }

  .af-item:hover {
    background: rgba(102, 126, 234, 0.05);
  }

  /* Timeline connector line */
  .af-item-line {
    position: absolute;
    left: 21px;
    top: 40px;
    bottom: -2px;
    width: 2px;
    background: rgba(102, 126, 234, 0.1);
  }

  .af-compact .af-item-line {
    left: 18px;
    top: 34px;
  }

  .af-time-group .af-item:last-child .af-item-line {
    display: none;
  }

  /* New item animation */
  .af-item-enter {
    animation: afSlideIn 0.5s ease-out;
  }

  @keyframes afSlideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .af-item-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }

  .af-compact .af-item-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
  }

  .af-item-content {
    flex: 1;
    min-width: 0;
  }

  .af-item-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 3px;
  }

  .af-item-title {
    font-size: 0.84rem;
    font-weight: 500;
    color: rgba(255,255,255,0.9);
    line-height: 1.35;
  }

  .af-compact .af-item-title {
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .af-item-time {
    font-size: 0.7rem;
    color: rgba(255,255,255,0.35);
    white-space: nowrap;
    flex-shrink: 0;
    padding-top: 2px;
  }

  .af-item-desc {
    font-size: 0.78rem;
    color: rgba(255,255,255,0.45);
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .af-item-meta {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
  }

  .af-item-project {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 4px;
    background: rgba(102, 126, 234, 0.12);
    color: #667eea;
    font-size: 0.68rem;
    font-weight: 500;
    transition: background 0.15s;
  }

  .af-item-project:hover {
    background: rgba(102, 126, 234, 0.22);
  }

  .af-item-actor {
    font-size: 0.68rem;
    color: rgba(255,255,255,0.4);
    font-style: italic;
  }

  .af-item-type-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  /* Load More */
  .af-load-more {
    display: block;
    width: 100%;
    padding: 12px;
    margin-top: 8px;
    background: rgba(102, 126, 234, 0.08);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 10px;
    color: #667eea;
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .af-load-more:hover {
    background: rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.4);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .af-filters {
      flex-direction: column;
      align-items: stretch;
    }

    .af-type-filters {
      overflow-x: auto;
      flex-wrap: nowrap;
      padding-bottom: 4px;
    }

    .af-type-pill {
      white-space: nowrap;
    }

    .af-item-header {
      flex-direction: column;
      gap: 2px;
    }

    .af-item-time {
      padding-top: 0;
    }
  }
`;

export default ActivityFeed;
